import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { Loaded } from "@/components/Loading";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { call, CALLABLES } from "@/services/callables";
import { formatDate } from "@/lib/format";
import {
  LICENCE_BANNER,
  licenceState,
  showsSelfServe,
  TIER_MAX_TEACHERS,
  TIER_ORDER,
  tierChange,
} from "./licence";
import type { BillingStatus, LegalStatus, LicenceTier } from "@/types";

/**
 * Billing.
 *
 * Three things this page has to get right, in order of how much they matter to
 * an institution reading it: that expiry deletes nothing, that a refused
 * downgrade says what would let it through, and that an institution billed by
 * invoice is not offered a checkout button it must not press.
 */
export function BillingPage() {
  const t = useT();
  const { locale } = useI18n();
  const [params] = useSearchParams();

  const status = useCallable<BillingStatus>(CALLABLES.getBillingStatus);
  const legal = useCallable<LegalStatus>(CALLABLES.getLegalStatus);

  const outcome = params.get("checkout");

  return (
    <SectionShell titleKey="billing.title">
      {outcome === "success" ? (
        <p className="mb-6 rounded-card border border-positive bg-positive-soft px-5 py-4 text-sm text-ink">
          {t("billing.checkout.waiting")}
        </p>
      ) : null}
      {outcome === "cancelled" ? (
        <p className="surface-inset mb-6 px-5 py-4 text-sm text-ink-soft">
          {t("billing.checkout.cancelled")}
        </p>
      ) : null}

      <Loaded state={status}>
        {(billing) => {
          const state = licenceState(billing, Date.now());
          const banner = LICENCE_BANNER[state];
          const outstanding = legal.data?.outstanding.length ?? 0;

          return (
            <>
              {banner ? (
                <div
                  role="status"
                  className={`mb-8 rounded-card border px-5 py-4 ${
                    banner.tone === "stop"
                      ? "border-danger bg-danger-soft"
                      : banner.tone === "warn"
                        ? "border-warning bg-warning-soft"
                        : "border-line bg-surface-muted"
                  }`}
                >
                  <p className="font-medium text-ink">
                    {t(banner.titleKey, {
                      date: formatDate(locale, billing.validUntil),
                    })}
                  </p>
                  {/*
                    The sentence that matters most on this page. An institution
                    reading a red banner fears losing its work first and its
                    lessons second.
                  */}
                  <p className="mt-1 text-sm text-ink-soft">{t(banner.bodyKey)}</p>
                </div>
              ) : null}

              <section className="max-w-2xl">
                <h2 className="section-title">{t("billing.plan.title")}</h2>
                <dl className="mt-3 grid grid-cols-[12rem_1fr] gap-y-2 text-sm">
                  <dt className="text-ink-muted">{t("billing.plan.tier")}</dt>
                  <dd className="text-ink">{t(`billing.tier.${billing.licenceTier}`)}</dd>

                  <dt className="text-ink-muted">{t("billing.plan.seats")}</dt>
                  <dd className="text-ink">
                    {t("billing.plan.seatsOf", {
                      used: billing.teacherSeatsUsed,
                      total: billing.teacherSeats,
                    })}
                  </dd>

                  <dt className="text-ink-muted">{t("billing.plan.renews")}</dt>
                  <dd className="text-ink">{formatDate(locale, billing.validUntil)}</dd>
                </dl>

                {billing.cancelAtPeriodEnd ? (
                  <p className="mt-2 text-sm text-warning">
                    {t("billing.plan.cancelAtPeriodEnd")}
                  </p>
                ) : null}
              </section>

              {showsSelfServe(billing) ? (
                <>
                  <Tiers billing={billing} outstandingDocuments={outstanding} />
                  <Portal />
                </>
              ) : null}

              <VatPanel status={billing} onChanged={status.reload} />
            </>
          );
        }}
      </Loaded>
    </SectionShell>
  );
}

function Tiers({
  billing,
  outstandingDocuments,
}: {
  billing: BillingStatus;
  outstandingDocuments: number;
}) {
  const t = useT();
  const { showError } = useToast();
  const [busy, setBusy] = useState<LicenceTier | undefined>(undefined);

  const checkout = async (tier: LicenceTier) => {
    setBusy(tier);
    try {
      const origin = window.location.origin;
      const result = await call<
        { tier: LicenceTier; successUrl: string; cancelUrl: string },
        { url: string }
      >(CALLABLES.createCheckoutSession, {
        tier,
        successUrl: `${origin}/billing?checkout=success`,
        cancelUrl: `${origin}/billing?checkout=cancelled`,
      });
      window.location.assign(result.url);
    } catch (error) {
      showError(error);
      setBusy(undefined);
    }
  };

  return (
    <section className="mt-12">
      <h2 className="section-title">{t("billing.tier.current")}</h2>
      <p className="mt-1 max-w-2xl text-sm text-ink-muted">{t("billing.tier.priceElsewhere")}</p>

      {/*
        Acceptance precedes payment. The data processing agreement is part of
        what an institution would be agreeing to, not an afterthought bolted on
        after the card has been charged.
      */}
      {outstandingDocuments > 0 ? (
        <div className="mt-4 max-w-2xl rounded-card border border-warning bg-warning-soft px-5 py-4">
          <p className="text-sm text-ink">{t("billing.checkout.legalFirst")}</p>
          <Link
            to="/legal"
            className="mt-3 inline-block text-sm font-medium text-ink underline underline-offset-2"
          >
            {t("billing.checkout.toLegal")}
          </Link>
        </div>
      ) : null}

      <ul className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {TIER_ORDER.map((tier) => {
          const change = tierChange(billing.licenceTier, tier, billing.teacherSeatsUsed);
          const isCurrent = tier === billing.licenceTier;
          const max = TIER_MAX_TEACHERS[tier];

          return (
            <li
              key={tier}
              className={`card flex flex-col p-5 ${isCurrent ? "border-brand" : ""}`}
            >
              <p className="section-title">{t(`billing.tier.${tier}`)}</p>
              <p className="mt-1 text-sm text-ink-muted">
                {max === 1
                  ? t("billing.tier.teachersOne")
                  : t("billing.tier.teachers", { count: max })}
              </p>

              <div className="mt-auto pt-5">
                {isCurrent ? (
                  <p className="text-sm font-medium text-brand-ink">{t("billing.tier.current")}</p>
                ) : change.allowed ? (
                  <Button
                    variant="secondary"
                    busy={busy === tier}
                    disabled={outstandingDocuments > 0}
                    onClick={() => checkout(tier)}
                  >
                    {t("billing.tier.choose", { tier: t(`billing.tier.${tier}`) })}
                  </Button>
                ) : (
                  /*
                    Refused with the numbers. "You cannot do that" leaves an
                    owner to work out what would let them; this says it.
                  */
                  <p className="text-sm text-danger">
                    {t("billing.tier.blocked", {
                      used: change.used,
                      max: change.max,
                      tier: t(`billing.tier.${tier}`),
                    })}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Portal() {
  const t = useT();
  const { showError } = useToast();
  const [busy, setBusy] = useState(false);

  return (
    <section className="mt-12 max-w-2xl">
      <h2 className="section-title">{t("billing.portal.title")}</h2>
      <p className="mt-1 text-sm text-ink-muted">{t("billing.portal.body")}</p>
      <Button
        variant="secondary"
        busy={busy}
        className="mt-4"
        onClick={async () => {
          setBusy(true);
          try {
            const result = await call<{ returnUrl: string }, { url: string }>(
              CALLABLES.createPortalSession,
              { returnUrl: `${window.location.origin}/billing` }
            );
            window.location.assign(result.url);
          } catch (error) {
            showError(error);
            setBusy(false);
          }
        }}
      >
        {t("billing.portal.open")}
      </Button>
    </section>
  );
}

/**
 * The VAT identification number.
 *
 * The same panel as institution settings, here because this is where somebody
 * looking at an invoice will go for it. An unvalidated number is treated as
 * absent and the line says so plainly — a field that looks like an entitlement
 * to zero-rating and is not is worse than no field.
 */
function VatPanel({ status, onChanged }: { status: BillingStatus; onChanged: () => void }) {
  const t = useT();
  const { locale } = useI18n();
  const { success, showError } = useToast();
  const [vatId, setVatId] = useState(status.vatId ?? "");
  const [busy, setBusy] = useState(false);

  const treatmentKey =
    status.vatTreatment === "reverse_charge"
      ? "institution.vat.validated"
      : status.vatTreatment === "outside_scope"
        ? "institution.vat.outsideScope"
        : status.vatId
          ? "institution.vat.charge"
          : "institution.vat.none";

  return (
    <section className="mt-12 max-w-2xl">
      <h2 className="section-title">{t("institution.vatId")}</h2>

      <label className="mt-3 block">
        <span className="sr-only">{t("institution.vatId")}</span>
        <input
          className="field-input"
          value={vatId}
          autoCapitalize="characters"
          spellCheck={false}
          aria-label={t("institution.vatId")}
          onChange={(event) => setVatId(event.target.value)}
        />
      </label>

      <p className="mt-2 text-sm text-ink-muted">
        {t(treatmentKey, {
          date: status.vatIdValidatedAt ? formatDate(locale, status.vatIdValidatedAt) : "",
        })}
      </p>

      <div className="mt-3 flex gap-2">
        <Button
          variant="secondary"
          busy={busy}
          disabled={vatId.trim().length === 0}
          onClick={async () => {
            setBusy(true);
            try {
              await call(CALLABLES.setVatId, { vatId: vatId.trim() });
              success("institution.saved");
              onChanged();
            } catch (error) {
              showError(error);
            } finally {
              setBusy(false);
            }
          }}
        >
          {t("institution.vat.revalidate")}
        </Button>

        {status.vatId ? (
          <Button
            variant="ghost"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await call(CALLABLES.clearVatId);
                setVatId("");
                success("institution.saved");
                onChanged();
              } catch (error) {
                showError(error);
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("institution.vat.remove")}
          </Button>
        ) : null}
      </div>
    </section>
  );
}
